import { ApiClientCore, isCoreApiError } from '@easyflow/ui-frontend';
import type { SessionPolicy, TenantRefreshResponseBody } from '@easyflow/shared';
import { AUTH_STORAGE_KEYS, clearAuthStorage } from '@/constants/auth';
import { persistSessionPolicy } from '@/lib/session-policy';

/** Evento finestra: nuovo access token disponibile (refresh proattivo o da 401). */
export const AUTH_TOKEN_REFRESHED_EVENT = 'auth:token-refreshed';
/** Evento finestra: sessione non più valida (refresh terminale o 401 non recuperabile). */
export const AUTH_UNAUTHORIZED_EVENT = 'auth:unauthorized';

export interface AuthTokenRefreshedDetail {
  readonly token: string;
  /** Presente quando il refresh ha portato una policy aggiornata dal server. */
  readonly sessionPolicy: SessionPolicy | null;
}

/**
 * Attesa prima di rileggere lo storage dopo un 401 sul refresh: un'altra tab
 * può aver ruotato il refresh token nello stesso istante (rotazione
 * single-use lato server) e aver già salvato la nuova coppia.
 */
const REFRESH_ROTATION_RETRY_DELAY_MS = 250;

/**
 * Client "bare" per i flussi di autenticazione (login/refresh/logout):
 * stesso core condiviso ma SENZA refreshHandler né onUnauthorized, così
 * il refresh non ricorre su se stesso e un 401 in login non spara il
 * dispatch `auth:unauthorized` due volte.
 */
export const authClient = new ApiClientCore({
  baseUrl: '/api',
  tokenStorageKey: AUTH_STORAGE_KEYS.TOKEN,
  onUnauthorized: () => undefined,
});

export function resolveStoredTenantSlug(): string | null {
  let tenantSlug = localStorage.getItem(AUTH_STORAGE_KEYS.TENANT_SLUG);
  if (!tenantSlug) {
    const userStr = localStorage.getItem(AUTH_STORAGE_KEYS.USER);
    if (userStr) {
      try {
        const user = JSON.parse(userStr) as { tenantSlug?: string };
        tenantSlug = user.tenantSlug ?? null;
      } catch {
        tenantSlug = null;
      }
    }
  }
  return tenantSlug;
}

function signalUnauthorized(): void {
  clearAuthStorage();
  apiClient.clearToken();
  window.dispatchEvent(new CustomEvent(AUTH_UNAUTHORIZED_EVENT));
}

function dispatchTokenRefreshed(detail: AuthTokenRefreshedDetail): void {
  window.dispatchEvent(new CustomEvent<AuthTokenRefreshedDetail>(AUTH_TOKEN_REFRESHED_EVENT, { detail }));
}

interface RefreshOutcome {
  readonly kind: 'ok' | 'unauthorized' | 'transient';
  readonly body?: TenantRefreshResponseBody;
}

async function postRefresh(tenantSlug: string, refreshToken: string): Promise<RefreshOutcome> {
  try {
    // Endpoint con slug esplicito: `shouldPrefixTenant` lo lascia intatto.
    const body = (await authClient.post(`/${tenantSlug}/refresh-token`, {
      refreshToken,
    })) as TenantRefreshResponseBody;
    return { kind: 'ok', body };
  } catch (err) {
    if (isCoreApiError(err) && err.status === 401) return { kind: 'unauthorized' };
    // Rete, 5xx, 429…: errore temporaneo, la sessione resta valida e il
    // prossimo tick dello scheduler riprova.
    return { kind: 'transient' };
  }
}

/**
 * Salva la nuova coppia (+ policy), aggiorna il client e notifica l'app.
 * Recinto: la risposta vale solo se la sessione che l'ha chiesta è ancora
 * quella in storage. Se nel frattempo c'è stato logout/scadenza (refresh
 * token rimosso) o una rotazione altrui, la risposta arrivata in ritardo NON
 * deve riaprire né sovrascrivere la sessione.
 */
function adoptRefreshedSession(body: TenantRefreshResponseBody, usedRefreshToken: string): string | null {
  const token = body.data?.token ?? body.token ?? null;
  if (!token) return null;
  const storedRefreshToken = localStorage.getItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN);
  if (storedRefreshToken !== usedRefreshToken) return null;
  const refreshToken = body.data?.refreshToken ?? body.refreshToken ?? null;
  const sessionPolicy = body.data?.sessionPolicy ?? null;
  localStorage.setItem(AUTH_STORAGE_KEYS.TOKEN, token);
  if (refreshToken) localStorage.setItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
  if (sessionPolicy) persistSessionPolicy(sessionPolicy);
  apiClient.setToken(token);
  dispatchTokenRefreshed({ token, sessionPolicy });
  return token;
}

/** Esito del rinnovo: `terminale` ha già pulito lo storage ed emesso `auth:unauthorized`. */
export type RefreshSessionResult =
  | { readonly kind: 'ok'; readonly token: string }
  | { readonly kind: 'terminal' }
  | { readonly kind: 'transient' };

function adoptOrTransient(body: TenantRefreshResponseBody | undefined, usedRefreshToken: string): RefreshSessionResult {
  const token = adoptRefreshedSession(body ?? {}, usedRefreshToken);
  if (token) return { kind: 'ok', token };
  // Sessione chiusa mentre il rinnovo era in volo: niente da adottare, niente da pulire oltre.
  if (localStorage.getItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN) === null) {
    apiClient.clearToken();
    return { kind: 'terminal' };
  }
  // 200 senza token (malformata) o rotazione altrui: non è una revoca.
  return { kind: 'transient' };
}

async function doRefresh(): Promise<RefreshSessionResult> {
  const currentRefreshToken = localStorage.getItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN);
  const tenantSlug = resolveStoredTenantSlug();
  if (!currentRefreshToken || !tenantSlug) {
    signalUnauthorized();
    return { kind: 'terminal' };
  }

  const first = await postRefresh(tenantSlug, currentRefreshToken);
  if (first.kind === 'ok') return adoptOrTransient(first.body, currentRefreshToken);
  if (first.kind === 'transient') return { kind: 'transient' };

  // 401: prima di considerarlo terminale, verifica una rotazione concorrente.
  await new Promise<void>((resolve) => {
    setTimeout(resolve, REFRESH_ROTATION_RETRY_DELAY_MS);
  });
  const retryRefreshToken = localStorage.getItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN);
  if (!retryRefreshToken) {
    // Un'altra tab ha già chiuso la sessione (logout/scadenza).
    signalUnauthorized();
    return { kind: 'terminal' };
  }
  if (retryRefreshToken !== currentRefreshToken) {
    // Rotazione fatta altrove: adotta l'access token già salvato dall'altra tab.
    const rotatedAccessToken = localStorage.getItem(AUTH_STORAGE_KEYS.TOKEN);
    if (!rotatedAccessToken) return { kind: 'transient' };
    apiClient.setToken(rotatedAccessToken);
    dispatchTokenRefreshed({ token: rotatedAccessToken, sessionPolicy: null });
    return { kind: 'ok', token: rotatedAccessToken };
  }

  const retry = await postRefresh(tenantSlug, retryRefreshToken);
  if (retry.kind === 'ok') return adoptOrTransient(retry.body, retryRefreshToken);
  if (retry.kind === 'transient') return { kind: 'transient' };
  signalUnauthorized();
  return { kind: 'terminal' };
}

let refreshPromise: Promise<RefreshSessionResult> | null = null;

/**
 * Rinnovo della sessione in single-flight con esito tipizzato: usato dal
 * bootstrap (che distingue un'interruzione temporanea da una sessione revocata).
 */
export function refreshSessionDetailed(): Promise<RefreshSessionResult> {
  if (!refreshPromise) {
    refreshPromise = doRefresh().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

/**
 * Stessa single-flight per il replay su 401 del core (`refreshHandler`), lo
 * scheduler `session-idle` (refresh proattivo) e il banner ("Mantieni sessione").
 * Ritorna il nuovo access token, o null se il rinnovo non è avvenuto
 * (terminale → `auth:unauthorized` già emesso; temporaneo → nulla cambia).
 */
export async function refreshSession(): Promise<string | null> {
  const result = await refreshSessionDetailed();
  return result.kind === 'ok' ? result.token : null;
}

/**
 * Il core, se il refreshHandler ritorna null, chiama SUBITO `onUnauthorized`.
 * Un rinnovo fallito per rete/5xx non è una revoca: le credenziali restano e
 * la richiesta fallisce con 401 (lo scheduler ritenta al tick successivo).
 * Su esito terminale lo storage è già stato pulito ed emesso l'evento.
 */
let suppressNextCoreUnauthorized = false;

async function coreRefreshHandler(): Promise<string | null> {
  const result = await refreshSessionDetailed();
  if (result.kind === 'ok') return result.token;
  suppressNextCoreUnauthorized = true;
  return null;
}

function onCoreUnauthorized(): void {
  if (suppressNextCoreUnauthorized) {
    suppressNextCoreUnauthorized = false;
    return;
  }
  signalUnauthorized();
}

/** ApiClient del verticale W3: core condiviso da @easyflow/ui-frontend. */
export const apiClient = new ApiClientCore({
  baseUrl: '/api',
  tokenStorageKey: AUTH_STORAGE_KEYS.TOKEN,
  refreshTokenStorageKey: AUTH_STORAGE_KEYS.REFRESH_TOKEN,
  // Il core fa UN replay della richiesta originale col token ritornato.
  refreshHandler: coreRefreshHandler,
  onUnauthorized: onCoreUnauthorized,
});
